import React, { useState, useMemo } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const OrderQueueTable = ({ orders, onOrderSelect, selectedOrderId, onBulkAction }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [venueFilter, setVenueFilter] = useState('all');
  const [selectedOrders, setSelectedOrders] = useState(new Set());

  const statusOptions = [
    { value: 'all', label: 'All Status' },
    { value: 'pending', label: 'Pending' },
    { value: 'executing', label: 'Executing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  const typeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'market', label: 'Market' },
    { value: 'limit', label: 'Limit' },
    { value: 'stop_loss', label: 'Stop Loss' },
    { value: 'stop_limit', label: 'Stop Limit' }
  ];

  const venueOptions = [
    { value: 'all', label: 'All Venues' },
    { value: 'nse', label: 'NSE' },
    { value: 'bse', label: 'BSE' },
    { value: 'binance', label: 'Binance' },
    { value: 'kucoin', label: 'KuCoin' }
  ];

  const filteredOrders = useMemo(() => {
    return orders?.filter(order => {
      const matchesSearch = order?.symbol?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                           order?.orderId?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesStatus = statusFilter === 'all' || order?.status === statusFilter;
      const matchesType = typeFilter === 'all' || order?.type === typeFilter;
      const matchesVenue = venueFilter === 'all' || order?.venue === venueFilter;
      
      return matchesSearch && matchesStatus && matchesType && matchesVenue;
    });
  }, [orders, searchTerm, statusFilter, typeFilter, venueFilter]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-warning bg-warning/10';
      case 'executing': return 'text-primary bg-primary/10';
      case 'completed': return 'text-success bg-success/10';
      case 'failed': return 'text-error bg-error/10';
      case 'cancelled': return 'text-muted-foreground bg-muted';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getElapsedTime = (timestamp) => {
    const now = new Date();
    const orderTime = new Date(timestamp);
    const diffMs = now - orderTime;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(new Set(filteredOrders.map(order => order.orderId)));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (orderId, checked) => {
    const newSelected = new Set(selectedOrders);
    if (checked) {
      newSelected?.add(orderId);
    } else {
      newSelected?.delete(orderId);
    }
    setSelectedOrders(newSelected);
  };

  const handleBulkAction = (action) => {
    if (selectedOrders?.size > 0) {
      onBulkAction(action, Array.from(selectedOrders));
      setSelectedOrders(new Set());
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header with Filters */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Order Queue</h3>
          <div className="flex items-center space-x-2">
            <div className="flex items-center space-x-1 text-sm text-muted-foreground">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse-subtle"></div>
              <span>Live Updates</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Input
            type="search"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
          <Select
            options={statusOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder="Filter by status"
          />
          <Select
            options={typeOptions}
            value={typeFilter}
            onChange={setTypeFilter}
            placeholder="Filter by type"
          />
          <Select
            options={venueOptions}
            value={venueFilter}
            onChange={setVenueFilter}
            placeholder="Filter by venue"
          />
        </div>

        {/* Bulk Actions */}
        {selectedOrders?.size > 0 && (
          <div className="mt-4 p-3 bg-muted rounded-lg flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">
              {selectedOrders?.size} orders selected
            </span>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('cancel')}
                iconName="X"
                iconSize={14}
              >
                Cancel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('priority')}
                iconName="ArrowUp"
                iconSize={14}
              >
                Priority
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleBulkAction('modify')}
                iconName="Edit"
                iconSize={14}
              >
                Modify
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left">
                <input
                  type="checkbox"
                  checked={selectedOrders?.size === filteredOrders?.length && filteredOrders?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Order ID</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Symbol</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Type</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Quantity</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Target Price</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Venue</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Elapsed</th>
              <th className="p-3 text-left text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders?.map((order) => (
              <tr
                key={order?.orderId}
                className={`border-b border-border hover:bg-muted/30 cursor-pointer transition-smooth ${
                  selectedOrderId === order?.orderId ? 'bg-primary/5' : ''
                }`}
                onClick={() => onOrderSelect(order)}
              >
                <td className="p-3">
                  <input
                    type="checkbox"
                    checked={selectedOrders?.has(order?.orderId)}
                    onChange={(e) => {
                      e?.stopPropagation();
                      handleSelectOrder(order?.orderId, e?.target?.checked);
                    }}
                    className="rounded border-border"
                  />
                </td>
                <td className="p-3">
                  <span className="text-sm font-mono text-foreground">{order?.orderId}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium text-foreground">{order?.symbol}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      order?.side === 'buy' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                    }`}>
                      {order?.side?.toUpperCase()}
                    </span>
                  </div>
                </td>
                <td className="p-3">
                  <span className="text-sm text-muted-foreground capitalize">{order?.type}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-foreground">{order?.quantity?.toLocaleString()}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-foreground">
                    {order?.venue?.includes('nse') || order?.venue?.includes('bse') ? '₹' : '$'}
                    {order?.targetPrice?.toLocaleString()}
                  </span>
                </td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${getStatusColor(order?.status)}`}>
                    {order?.status?.charAt(0)?.toUpperCase() + order?.status?.slice(1)}
                  </span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-muted-foreground uppercase">{order?.venue}</span>
                </td>
                <td className="p-3">
                  <span className="text-sm text-muted-foreground">{getElapsedTime(order?.timestamp)}</span>
                </td>
                <td className="p-3">
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle modify action
                      }}
                      iconName="Edit"
                      iconSize={14}
                      className="text-muted-foreground hover:text-foreground"
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e?.stopPropagation();
                        // Handle cancel action
                      }}
                      iconName="X"
                      iconSize={14}
                      className="text-muted-foreground hover:text-error"
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filteredOrders?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Search" size={48} className="mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No orders found matching your criteria</p>
        </div>
      )}
    </div>
  );
};

export default OrderQueueTable;